// Écran urbanisme (lot L5-C2, M18) : carte du territoire avec les zones des plans non abrogés et les demandes
// localisées numérotées comme la liste voisine, puis liste des demandes (dossiers M05 des démarches
// d'urbanisme configurées) avec type, zone, avis rendus et avancement. Charte et composants existants.
import type { ResumeDemandeUrbanisme } from '@cc/contrats';
import { Bandeau, CarteTerritoire, EtatVide, Lien, PuceEtat, Squelette, Tableau, numeroMarqueur, type ColonneTableau } from '@cc/ui';
import { MapPinned } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useEchellesTerritoire } from '../marches/carte';
import { useCarteUrbanisme, useDemandes } from './api';
import { libelleAvancement, marqueursDemandes, surfacesZones, TON_AVANCEMENT } from './logique';
import { NavigationUrbanisme } from './NavigationUrbanisme';
import marches from '../marches/marches.module.css';
import styles from '../achats/achats.module.css';

export const dateCourte = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { dateStyle: 'medium', timeZone: 'Africa/Libreville' } as Intl.DateTimeFormatOptions) : 'non enregistrée');

export function PageDemandes() {
  const t = useEchellesTerritoire();
  const couche = useCarteUrbanisme(t.bbox);
  const demandes = useDemandes();
  const [actif, setActif] = useState<string | null>(null);
  const marqueurs = useMemo(() => marqueursDemandes(couche.data), [couche.data]);
  const surfaces = useMemo(() => surfacesZones(couche.data), [couche.data]);
  const liste = demandes.data?.elements ?? [];
  const colonnes: ColonneTableau<ResumeDemandeUrbanisme>[] = [
    { id: 'numero', entete: 'Dossier', enTeteLigne: true, masquable: false, cellule: (d) => <Lien href={`/urbanisme/demandes/${d.id}`} className="cc-mono">{d.numero ?? 'Sans numéro'}</Lien>, valeurTri: (d) => d.numero ?? '' },
    { id: 'type', entete: 'Type de demande', cellule: (d) => d.type.libelle, valeurTri: (d) => d.type.libelle },
    { id: 'demandeur', entete: 'Demandeur', cellule: (d) => d.demandeur ?? 'non renseigné' },
    { id: 'zone', entete: 'Zone', cellule: (d) => (d.zone ? `${d.zone.code} · ${d.zone.libelle}` : d.position ? 'Hors zone' : 'Non localisée') },
    { id: 'avis', entete: 'Avis requis rendus', numerique: true, cellule: (d) => `${d.avis.rendus} sur ${d.avis.requis}`, valeurTri: (d) => d.avis.rendus },
    { id: 'enregistre', entete: 'Enregistrée le', cellule: (d) => dateCourte(d.enregistre_le), valeurTri: (d) => d.enregistre_le ?? '' },
    { id: 'avancement', entete: 'Avancement', cellule: (d) => <PuceEtat ton={TON_AVANCEMENT[d.avancement]} libelle={libelleAvancement(d.avancement)} /> },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Urbanisme"
        titre="Demandes d'urbanisme"
        description="Demandes déposées par les démarches d'urbanisme configurées : zone retenue, avis des services, inspections et décision de l'autorité configurée. Aucun titre foncier n'est délivré."
      />
      <NavigationUrbanisme actif="demandes" />
      <div className={marches.disposition}>
        <div className={marches.carteCadre}>
          {t.echelles?.mention ? <Bandeau type="info">{t.echelles.mention}</Bandeau> : null}
          {couche.error ? <Bandeau type="info">La couche de l&apos;urbanisme n&apos;est pas disponible (droits ou module inactif) : la carte s&apos;affiche sans zones ni demandes.</Bandeau> : null}
          {t.pret && t.echelles && t.echelle ? (
            <CarteTerritoire titre="Zones et demandes d'urbanisme" echelles={t.echelles.echelles} echelle={t.echelle} onEchelle={t.setEchelle} marqueurs={marqueurs} marqueurActif={actif} onMarqueur={setActif} surfaces={surfaces} libellesTon={{ ouvert: 'Demande en instruction', autre: 'Demande décidée ou terminée' }} libelleObjets={{ singulier: 'demande localisée', pluriel: 'demandes localisées' }} hauteur="min(60vh, 520px)" />
          ) : (
            <Squelette hauteurBloc={400} libelle="Chargement de la carte" />
          )}
          <p className={marches.aide}>Les emprises des zones des plans en projet ou en vigueur sont dessinées ; chaque demande localisée est marquée.</p>
        </div>
        <aside className={marches.cote} aria-label="Demandes sur la carte">
          <ol className={marches.listeCarte} aria-label="Demandes sur la carte">
            {marqueurs.map((m) => (
              <li key={m.id}>
                <button type="button" className={marches.elementCarte} aria-pressed={actif === m.id} onClick={() => setActif(actif === m.id ? null : m.id)}>
                  <span className={marches.numero} aria-hidden>{numeroMarqueur(m.numero)}</span>
                  <span>{m.libelle}</span>
                </button>
              </li>
            ))}
          </ol>
          {t.bbox && marqueurs.length === 0 && !couche.isPending ? <p className={marches.aide}>Aucune demande localisée dans cette emprise.</p> : null}
        </aside>
      </div>
      <EtatRequete chargement={demandes.isPending} erreur={demandes.error} onReessayer={() => void demandes.refetch()} lot="L5-C2" fonction="La liste des demandes d'urbanisme">
        <section className={styles.bloc} aria-labelledby="titre-demandes">
          <h2 id="titre-demandes">Demandes</h2>
          {liste.length === 0 ? (
            <EtatVide titre="Aucune demande" icone={MapPinned}><p>Aucune demande d&apos;urbanisme n&apos;est enregistrée ou visible avec vos droits.</p></EtatVide>
          ) : (
            <Tableau legende="Demandes d'urbanisme" colonnes={colonnes} lignes={liste} cleLigne={(d) => d.id} cleColonnes="urbanisme-demandes" />
          )}
        </section>
      </EtatRequete>
    </>
  );
}
